import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  LayoutDashboard, CalendarDays, Users, HardHat, MapPin, FileBarChart2, Archive,
  LogOut, Menu, X, ChevronRight, Wallet, PenToolIcon   // Wallet eklendi
} from 'lucide-react';

const SEKMELER = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'muhasebe', label: 'Muhasebe', icon: Wallet }, // Muhasebe eklendi
  { id: 'takvim', label: 'Takvim', icon: CalendarDays },
  { id: 'musteriler', label: 'Müşteriler', icon: Users },
  { id: 'calisanlar', label: 'Çalışanlar', icon: HardHat },
  { id: 'santiyeler', label: 'Şantiyeler', icon: MapPin },
  { id: 'rapor', label: 'Rapor', icon: FileBarChart2 },
  { id: 'arsiv', label: 'Arşiv', icon: Archive },
  { id: 'araclar', label: 'Araçlar', icon: PenToolIcon  },
];

const Sidebar = ({
  sekme, setSekme, ustaBadge, logout, isCollapsed, setIsCollapsed, onNavClick
}) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleNavClick = (id) => {
    setSekme(id);
    setIsMobileMenuOpen(false);
    onNavClick?.();
  };

  return (
    <>
      {/* --- MASAÜSTÜ SIDEBAR --- */}
      <aside className={`hidden md:flex bg-stone-900 border-r border-stone-800 flex-col flex-shrink-0 transition-all duration-300 ease-in-out overflow-hidden ${isCollapsed ? 'w-20' : 'w-64'}`}>
        {/* Logo */}
        <div className="px-4 py-4 border-b border-stone-800 flex items-center justify-between">
          <div className={`text-xl font-bold tracking-[6px] text-white transition-all ${isCollapsed ? 'hidden' : ''}`}>AGE</div>
          {!isCollapsed && (
            <div className="text-[11px] tracking-wide uppercase text-stone-500">Müşteri Takip</div>
          )}
        </div>

        {/* Navigasyon */}
        <nav className="px-2 py-3 space-y-1 flex-1 overflow-y-auto">
          {SEKMELER.map(s => {
            const Icon = s.icon;
            const aktif = sekme === s.id || (s.id === 'musteriler' && sekme === 'musteri-detay');
            return (
              <button key={s.id}
                onClick={() => handleNavClick(s.id)}
                className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg text-left transition-all group hover:bg-stone-800/70 ${aktif ? 'bg-stone-800 text-white shadow-md' : 'text-stone-400 hover:text-white'} ${isCollapsed ? 'justify-center px-2.5 py-2.5 gap-0' : ''}`}
                title={isCollapsed ? s.label : undefined}
              >
                <Icon size={isCollapsed ? 20 : 16} className="flex-shrink-0" />
                {!isCollapsed && <span className="text-sm font-medium">{s.label}</span>}
                {s.id === 'calisanlar' && ustaBadge > 0 && !isCollapsed && (
                  <span className="ml-auto text-[10px] font-bold bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center shadow-md">
                    {ustaBadge > 9 ? '9+' : ustaBadge}
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        {/* Alt Toggle ve Çıkış Bölümü */}
        <div className="p-3 border-t border-stone-800 space-y-2">
          <div className={`flex items-center p-1 bg-stone-800/30 hover:bg-stone-700 rounded-xl transition-all text-stone-300 hover:text-white group ${isCollapsed ? 'flex-col space-y-2' : 'justify-between'}`}>
            <button
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="p-2.5 rounded-lg hover:bg-stone-700 transition-all flex items-center justify-center"
            >
              {isCollapsed ? <ChevronRight size={20} /> : <Menu size={20} />}
            </button>
            <button
              onClick={logout}
              className="p-2.5 text-stone-400 hover:text-red-400 hover:bg-stone-700 rounded-lg transition-all"
            >
              <LogOut size={20} />
            </button>
          </div>
        </div>
      </aside>

      {/* --- MOBİL MENÜ BUTONU (FAB) --- */}
      <div className="md:hidden fixed bottom-6 right-6 z-50">
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="bg-stone-900 text-white p-4 rounded-full shadow-2xl border border-stone-700 active:scale-95 transition-transform"
        >
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* --- MOBİL MENÜ PANELİ --- */}
      {isMobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-40 flex flex-col justify-end bg-black/60 backdrop-blur-sm p-4 transition-all">
          <div className="bg-stone-900 rounded-2xl p-2 mb-20 shadow-xl border border-stone-800 animate-in slide-in-from-bottom-5">
            <div className="grid grid-cols-3 gap-2 p-2">
              {SEKMELER.map(s => {
                const Icon = s.icon;
                const aktif = sekme === s.id;
                return (
                  <button
                    key={s.id}
                    onClick={() => handleNavClick(s.id)}
                    className={`flex flex-col items-center justify-center gap-2 p-4 rounded-xl transition-all ${aktif ? 'bg-stone-800 text-white' : 'text-stone-400'}`}
                  >
                    <Icon size={24} />
                    <span className="text-[10px] font-medium">{s.label}</span>
                  </button>
                );
              })}
              <button
                onClick={logout}
                className="flex flex-col items-center justify-center gap-2 p-4 rounded-xl text-red-400"
              >
                <LogOut size={24} />
                <span className="text-[10px] font-medium">Çıkış</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

Sidebar.propTypes = {
  sekme: PropTypes.string.isRequired,
  setSekme: PropTypes.func.isRequired,
  ustaBadge: PropTypes.number,
  logout: PropTypes.func,
  isCollapsed: PropTypes.bool,
  setIsCollapsed: PropTypes.func,
  onNavClick: PropTypes.func
};

export default Sidebar;